/**
 * Help & feedback's right panel (TMMAPP-0142 U2 items 12, 13, 13a): in place of the privacy panel,
 * the person's help and tickets, all free and without AI.
 * - **Help:** search and browse the help library (`HelpBrowser`).
 * - **Support cases** and **Feature requests:** the person's tickets, each with its number
 *   (`SC-1001`), title, status chip and last update; New ticket; a ticket's view (`TicketView`).
 * - **Announcements:** the team's announcements, the unread count as the tab's badge.
 * - **"What the assistant saw"** keeps this session's AI disclosure reachable (MM-D-94).
 * A ticket link (`?ticket=`) or an AI answer's citation arrives as `request`.
 *
 * **Locked** (M1): standing without the session. Help, the announcements and the tickets, which
 * tmm-api's lock allows (TMMAPI-0186 U1 item 7, TMMAPP-0153 item 18); the disclosure is a session
 * read the lock refuses. It opens on the tickets. New ticket offers a support case only, and a reply
 * attaches nothing (uploads are locked).
 *
 * **"Merged into"** opens the canonical ticket only when it is in the person's own list (M4).
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Link,
  List,
  ListItemButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { ticketLabel, ticketsApi, type Ticket, type TicketType } from '@/api/tickets';
import { ErrorState } from '@/components/ErrorState';
import { AddIcon } from '@/components/icons';
import { Pill } from '@/components/Pill';
import { StatusChip } from '@/components/StatusChip';
import { AnnouncementList } from '@/features/announcements/AnnouncementList';
import { useAnnouncements } from '@/features/announcements/announcementsContext';
import { formatInstant, formatNumber } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { useNotify } from '@/shell/NotifyProvider';
import { resolveTokens } from '@/theme/theme';
import { HelpBrowser, type HelpRequest } from './HelpBrowser';
import { NewTicketDialog } from './NewTicketDialog';
import { TicketView } from './TicketView';

type SupportTab = 'help' | 'cases' | 'requests' | 'announcements';

/** Something to open from outside: an article (an AI citation) or a ticket (a link). */
export type SupportRequest =
  | { kind: 'article'; id: string; nonce: number }
  | { kind: 'ticket'; number: number; nonce: number };

const TAB_LABELS: Record<SupportTab, string> = {
  help: 'support.tabs.help',
  cases: 'support.tabs.cases',
  requests: 'support.tabs.requests',
  announcements: 'support.tabs.announcements',
};

const TAB_TYPE: Record<'cases' | 'requests', TicketType> = {
  cases: 'SUPPORT_CASE',
  requests: 'FEATURE_REQUEST',
};

function TicketRows({ tickets, onOpen }: { tickets: Ticket[]; onOpen: (n: number) => void }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const zone = useMe().user.timezone ?? 'UTC';
  return (
    <List disablePadding sx={{ listStyle: 'none', m: 0 }} data-ticket-list="">
      {tickets.map((ticket) => {
        const waiting = ticket.status === 'WAITING_ON_YOU';
        let updated = ticket.updatedAt;
        try {
          updated = formatInstant(ticket.updatedAt, locale, zone, { dateStyle: 'medium' });
        } catch {
          /* the served instant as is */
        }
        return (
          <Box component="li" key={ticket.number} sx={{ borderBottom: `1px solid ${tokens.line}` }}>
            <ListItemButton
              onClick={() => onOpen(ticket.number)}
              data-ticket-row={ticket.number}
              data-waiting={waiting ? '' : undefined}
              sx={{
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.5,
                py: 1.25,
                ...(waiting ? { bgcolor: tokens.selected } : {}),
              }}
            >
              <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography component="span" sx={{ fontSize: 12, color: tokens.text2 }}>
                  {ticketLabel(ticket)}
                </Typography>
                <StatusChip domain="ticket" status={ticket.status} />
              </Stack>
              <Typography
                component="span"
                sx={{
                  fontSize: 14,
                  fontWeight: waiting ? 600 : 500,
                  color: tokens.text,
                  overflowWrap: 'anywhere',
                }}
              >
                {ticket.title}
              </Typography>
              <Typography component="span" sx={{ fontSize: 12, color: tokens.text2 }}>
                {t('support.tickets.updated', { when: updated })}
              </Typography>
            </ListItemButton>
          </Box>
        );
      })}
    </List>
  );
}

export function SupportPanel({
  headingId,
  supportSessionId,
  request,
  ticketsVersion,
  onShowPrivacy,
  close,
  locked = false,
}: {
  headingId: string;
  /** The person's Help & feedback session (a reply's files are uploaded there); null: no files. */
  supportSessionId: number | null;
  request?: SupportRequest | null | undefined;
  /** Bumped when a ticket was filed elsewhere (the chat's draft): the list re-reads. */
  ticketsVersion: number;
  /** "What the assistant saw"; absent where there is no session (locked). */
  onShowPrivacy?: (() => void) | undefined;
  /** The drawer's close control, at the narrower layouts. */
  close?: ReactNode;
  /** A locked member: the tickets alone (M1). */
  locked?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const notify = useNotify();
  const announcements = useAnnouncements();
  const [tab, setTab] = useState<SupportTab>(locked ? 'cases' : 'help');
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [ticketsError, setTicketsError] = useState<unknown>(null);
  const [openTicket, setOpenTicket] = useState<number | null>(null);
  const [newTicket, setNewTicket] = useState<TicketType | null>(null);
  const [helpRequest, setHelpRequest] = useState<HelpRequest | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const loadTickets = useCallback(async () => {
    try {
      setTickets(await ticketsApi.list());
      setTicketsError(null);
    } catch (error) {
      setTicketsError(error);
    }
  }, []);

  useEffect(() => {
    queueMicrotask(() => void loadTickets());
  }, [loadTickets, ticketsVersion, reloadKey]);

  // A request from outside switches to what it names.
  const [handled, setHandled] = useState<number | null>(null);
  if (request !== null && request !== undefined && request.nonce !== handled) {
    setHandled(request.nonce);
    if (request.kind === 'article') {
      setTab('help');
      setHelpRequest({ articleId: request.id, nonce: request.nonce });
    } else {
      const known = tickets?.find((ticket) => ticket.number === request.number);
      setTab(known?.type === 'FEATURE_REQUEST' ? 'requests' : 'cases');
      setOpenTicket(request.number);
    }
  }

  function chooseTab(next: SupportTab) {
    setTab(next);
    setOpenTicket(null);
  }

  const unread = announcements?.unreadCount ?? 0;
  const ticketTab = tab === 'cases' || tab === 'requests' ? tab : null;
  const shownTickets =
    ticketTab === null || tickets === null
      ? null
      : tickets.filter((ticket) => ticket.type === TAB_TYPE[ticketTab]);

  let content: ReactNode;
  if (tab === 'help') {
    content = <HelpBrowser request={helpRequest} />;
  } else if (tab === 'announcements') {
    content = announcements !== null ? <AnnouncementList announcements={announcements} /> : null;
  } else if (openTicket !== null) {
    content = (
      <TicketView
        key={openTicket}
        number={openTicket}
        supportSessionId={locked ? null : supportSessionId}
        canOpen={(number) => tickets?.some((ticket) => ticket.number === number) ?? false}
        onBack={() => setOpenTicket(null)}
        onOpenTicket={setOpenTicket}
        onChanged={() => setReloadKey((key) => key + 1)}
        onLoaded={(ticket) => setTab(ticket.type === 'FEATURE_REQUEST' ? 'requests' : 'cases')}
      />
    );
  } else {
    content = (
      <Stack sx={{ gap: 1 }}>
        <Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setNewTicket(ticketTab === null ? 'SUPPORT_CASE' : TAB_TYPE[ticketTab])}
          >
            {t('support.tickets.new')}
          </Button>
        </Box>
        {ticketsError !== null && tickets === null ? (
          <ErrorState
            variant="section"
            error={ticketsError}
            what={t('support.tickets.loadError')}
            onRetry={() => void loadTickets()}
          />
        ) : shownTickets === null ? (
          <Typography sx={{ fontSize: 14, color: tokens.text2 }}>{t('common.loading')}</Typography>
        ) : shownTickets.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: tokens.text2 }} data-tickets-empty="">
            {t('support.tickets.empty')}
          </Typography>
        ) : (
          <TicketRows tickets={shownTickets} onOpen={setOpenTicket} />
        )}
      </Stack>
    );
  }

  return (
    <Stack sx={{ minHeight: 0 }} data-support-panel="">
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 2, pb: 1 }}
      >
        <Typography
          id={headingId}
          tabIndex={-1}
          variant="h2"
          sx={{ fontSize: 16, fontWeight: 600 }}
        >
          {t('support.panelTitle')}
        </Typography>
        {close}
      </Stack>
      {onShowPrivacy !== undefined && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Link
            component="button"
            type="button"
            onClick={onShowPrivacy}
            data-what-assistant-saw=""
            sx={{ fontSize: 13 }}
          >
            {t('support.whatAssistantSaw')}
          </Link>
        </Box>
      )}
      <Tabs
        value={tab}
        onChange={(_event, next: SupportTab) => chooseTab(next)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label={t('support.tabsLabel')}
        sx={{ px: 1, borderBottom: `1px solid ${tokens.line}` }}
      >
        <Tab value="help" label={t(TAB_LABELS.help)} />
        <Tab value="cases" label={t(TAB_LABELS.cases)} />
        <Tab value="requests" label={t(TAB_LABELS.requests)} />
        {announcements !== null && (
          <Tab
            value="announcements"
            data-announcements-tab=""
            label={
              <Box
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
              >
                {t(TAB_LABELS.announcements)}
                {unread > 0 && (
                  <Box component="span" data-unread={unread}>
                    <Pill wash="brand">
                      <span aria-hidden>{formatNumber(unread, locale)}</span>
                      <Box component="span" sx={visuallyHidden}>
                        {t('support.announcements.unreadCount', { count: unread })}
                      </Box>
                    </Pill>
                  </Box>
                )}
              </Box>
            }
          />
        )}
      </Tabs>
      <Box sx={{ p: 2 }} role="tabpanel" aria-label={t(TAB_LABELS[tab])}>
        {content}
      </Box>
      {newTicket !== null && (
        <NewTicketDialog
          initialType={locked ? 'SUPPORT_CASE' : newTicket}
          types={locked ? ['SUPPORT_CASE'] : undefined}
          onClose={() => setNewTicket(null)}
          onFiled={(ticket) => {
            notify(t('support.tickets.filed', { number: ticketLabel(ticket) }));
            setTab(ticket.type === 'FEATURE_REQUEST' ? 'requests' : 'cases');
            setOpenTicket(ticket.number);
            setReloadKey((key) => key + 1);
          }}
        />
      )}
    </Stack>
  );
}
